import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  FiGrid,
  FiBookOpen,
  FiCreditCard,
  FiLayers,
  FiKey,
  FiArrowUpRight,
  FiArrowDownLeft,
  FiPlus,
  FiTrendingUp,
  FiDollarSign,
  FiShield,
} from "react-icons/fi";
import ToolsHeader from "./ToolsHeader";
import {
  getAllKhatabooks,
  listCashbooks,
  listProjects,
  getOverallProjectsLedgerSummary,
  listLeases,
} from "../../api/tools/toolsApi";
import "./Tools.css";

const ToolsHub = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const location = useLocation();
  const currentPath = location.pathname;

  let resolvedBase = "/partner/tools";
  if (currentPath.startsWith("/customer/tools") || currentPath.startsWith("/customer/")) {
    resolvedBase = "/customer/tools";
  } else if (currentPath.startsWith("/admin/tools") || currentPath.startsWith("/admin/")) {
    resolvedBase = "/admin/tools";
  } else if (currentPath.startsWith("/tools")) {
    resolvedBase = "/tools";
  }

  const [metrics, setMetrics] = useState({
    khatabooksCount: 0,
    cashbooksCount: 0,
    projectsCount: 0,
    leasesCount: 0,
    overallCredit: 0,
    overallDebit: 0,
    loading: true,
  });

  useEffect(() => {
    if (!token) return;
    let isMounted = true;

    const fetchOverviewStats = async () => {
      try {
        const [kbRes, cbRes, projRes, sumRes, leaseRes] =
          await Promise.allSettled([
            getAllKhatabooks(token),
            listCashbooks(token, { page_number: 1, page_size: 10 }),
            listProjects(token, { page_number: 1, page_size: 10 }),
            getOverallProjectsLedgerSummary(token),
            listLeases(token, { page_number: 1, page_size: 10 }),
          ]);

        if (!isMounted) return;

        // Response keys as the backend returns them; totals use totalRecord so counts are not capped by page_size
        const fromList = (res, key) => (res.status === "fulfilled" ? res.value?.[key] || [] : []);
        const countOf = (res, key) =>
          res.status === "fulfilled" ? Number(res.value?.totalRecord) || fromList(res, key).length : 0;

        const summary = sumRes.status === "fulfilled" ? sumRes.value || {} : {};

        setMetrics({
          khatabooksCount: fromList(kbRes, "khatabooks").length,
          cashbooksCount: countOf(cbRes, "cashBooks"),
          projectsCount: countOf(projRes, "projects"),
          leasesCount: countOf(leaseRes, "leases"),
          overallCredit: summary?.totalCredit || 0,
          overallDebit: summary?.totalDebit || 0,
          loading: false,
        });
      } catch (e) {
        if (isMounted) {
          setMetrics((prev) => ({ ...prev, loading: false }));
        }
      }
    };

    fetchOverviewStats();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const toolsList = [
    {
      id: "khatabook",
      title: "Khatabook",
      description:
        "Manage customer credits and debits, track who you gave money/goods to, who owes you, and record payment transactions.",
      icon: FiBookOpen,
      iconTheme: "tool-hub-card__icon-box--blue",
      to: `${resolvedBase}/khatabook`,
      countLabel: `${metrics.khatabooksCount} Active Books`,
      badgeText: "Credit / Debit Ledger",
    },
    {
      id: "cashbook",
      title: "Cashbook",
      description:
        "Record daily site and office cash entries in real-time, categorize cash in (credit) vs cash out (debit), and view net balance.",
      icon: FiCreditCard,
      iconTheme: "tool-hub-card__icon-box--emerald",
      to: `${resolvedBase}/cashbook`,
      countLabel: `${metrics.cashbooksCount} Cashbooks`,
      badgeText: "Daily Cash Flow",
    },
    {
      id: "projects",
      title: "Project Ledger & Management",
      description:
        "Manage interior, architectural, and renovation projects with customer advances, contractor payments, and site notes.",
      icon: FiLayers,
      iconTheme: "tool-hub-card__icon-box--indigo",
      to: `${resolvedBase}/projects`,
      countLabel: `${metrics.projectsCount} Projects`,
      badgeText: "Project Ledgers & Notes",
    },
    {
      id: "leases",
      title: "Lease Management",
      description:
        "Handle residential and commercial property leases, monthly rental agreements, security deposits, and tenant correspondence.",
      icon: FiKey,
      iconTheme: "tool-hub-card__icon-box--amber",
      to: `${resolvedBase}/leases`,
      countLabel: `${metrics.leasesCount} Leases`,
      badgeText: "Rental Agreements",
    },
  ];

  return (
    <div className="tools-container">
      <ToolsHeader
        title="Business Tools Hub"
        subtitle="Access all your essential financial, project, and property management tools in one integrated dashboard."
      />

      {/* Overview Stat Cards */}
      <div className="tools-stats-grid">
        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--blue">
            <FiGrid />
          </div>
          <div>
            <div className="tools-stat-card__label">Active Tools Suite</div>
            <div className="tools-stat-card__value text-primary">4 Tools Ready</div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--green">
            <FiArrowDownLeft />
          </div>
          <div>
            <div className="tools-stat-card__label">Total Advances / Received</div>
            <div className="tools-stat-card__value text-success">
              ₹{Number(metrics.overallCredit).toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--red">
            <FiArrowUpRight />
          </div>
          <div>
            <div className="tools-stat-card__label">Total Project Expenses</div>
            <div className="tools-stat-card__value text-danger">
              ₹{Number(metrics.overallDebit).toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--purple">
            <FiShield />
          </div>
          <div>
            <div className="tools-stat-card__label">Secure & Synchronized</div>
            <div className="tools-stat-card__value text-dark">Live Sync</div>
          </div>
        </div>
      </div>

      {/* Tool Cards Grid */}
      <div className="tools-hub-grid">
        {toolsList.map((tool) => {
          const Icon = tool.icon;
          return (
            <Link key={tool.id} to={tool.to} className="tool-hub-card">
              <div>
                <div className="tool-hub-card__header">
                  <div className={`tool-hub-card__icon-box ${tool.iconTheme}`}>
                    <Icon />
                  </div>
                  <span className="tools-badge tools-badge--primary">
                    {tool.badgeText}
                  </span>
                </div>

                <h3 className="tool-hub-card__title">{tool.title}</h3>
                <p className="tool-hub-card__desc">{tool.description}</p>
              </div>

              <div className="tool-hub-card__footer">
                <span className="text-muted fw-semibold">{tool.countLabel}</span>
                <span className="d-flex align-items-center gap-1">
                  Launch Tool &rarr;
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ToolsHub;
